import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useWindowSize } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import { FixedSizeList as List } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

import { useFilter } from './context/filterContext';
import { useCatalog } from './context/catalogContext';
import CatalogTracksItem from './CatalogTracksItem';
import Loader from '../../components/Loader';
import { setPopover } from '../../store/reducers/userListSlice';

const StyledLoader = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999;
  background: rgba(255,255,255,.3);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CatalogTracks = ({ height }) => {
  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const dispatch = useDispatch();

  const { getFilters, name } = useFilter();
  const { getCurrentPageData } = useCatalog();
  const { tracks, pageNav, isLoading } = useSelector((state) => state.catalog);

  const filters = useMemo(() => getFilters(), [getFilters]);

  const isInitialLoadedRef = useRef(false);
  const prevFiltersRef = useRef(null);

  useEffect(() => {
    if (isInitialLoadedRef.current) return;

    isInitialLoadedRef.current = true;
    prevFiltersRef.current = JSON.stringify(filters);

    getCurrentPageData({
      page: 1,
      filters,
      name,
    });
  }, [filters, name, getCurrentPageData]);


  useEffect(() => {
    if (!isInitialLoadedRef.current) return;

    const nextFilters = JSON.stringify(filters);
    if (prevFiltersRef.current === nextFilters) return;

    prevFiltersRef.current = nextFilters;

    document
      .getElementById('scrollableDiv')
      ?.scrollTo({ top: 0, behavior: 'smooth' });

    getCurrentPageData({
      page: 1,
      filters,
      name,
    });
  }, [filters, name, getCurrentPageData]);

  const loadMoreItems = useCallback(() => {
    if (!isInitialLoadedRef.current) return;
    if (isLoading) return;
    if (pageNav.curPage >= pageNav.allPage) return;

    return getCurrentPageData({
      page: pageNav.curPage + 1,
      filters,
      infinity: true,
      name,
    });
  }, [
    isLoading,
    pageNav.curPage,
    pageNav.allPage,
    filters,
    name,
    getCurrentPageData,
  ]);

  const isItemLoaded = (index) => index < tracks.length;

  const handleScroll = useCallback(() => {
    dispatch(setPopover(null));
  }, [dispatch]);

  const scrollHeight = height || windowHeight - 305;
  const ITEM_HEIGHT = windowWidth < 768 ? 70 : 108;

  const itemCount =
    tracks.length + (pageNav.curPage < pageNav.allPage ? 1 : 0);

  return (
    <div>
      {isLoading && (
        <StyledLoader>
          <Loader />
        </StyledLoader>
      )}

      <div
        id="scrollableDiv"
        className="Catalog__content-scroll-container mt-4"
        style={{ height: scrollHeight, overflow: 'auto' }}
        onScroll={handleScroll}
      >
        {!tracks.length && !isLoading ? (
          <div className="cart__list-header" style={{ fontSize: 18 }}>
            <div>Треки не найдены</div>
          </div>
        ) : (
          <InfiniteLoader
            isItemLoaded={isItemLoaded}
            itemCount={itemCount}
            loadMoreItems={loadMoreItems}
          >
            {({ onItemsRendered, ref }) => (
              <List
                ref={ref}
                height={scrollHeight}
                itemCount={tracks.length}
                itemSize={ITEM_HEIGHT}
                width="100%"
                onItemsRendered={onItemsRendered}
                itemKey={(index) => {
                  const track = tracks[index];
                  return track ? track.id : `loading-${index}`;
                }}
              >
                {({ index, style }) => {
                  const track = tracks[index];
                  if (!track) return null;

                  const num = index < 9 ? `0${index + 1}` : index + 1;

                  return (
                    <div style={style}>
                      <CatalogTracksItem
                        key={track.id}
                        track={track}
                        num={num}
                      />
                    </div>
                  );
                }}
              </List>
            )}
          </InfiniteLoader>
        )}
      </div>
    </div>
  );
};

export default CatalogTracks;